import type React from "react"
import LocaleLink from "@/lib/i18n/locale-link"
import { cn } from "@/lib/utils"

interface CommonViewMoreButtonProps {
	children: React.ReactNode
	href?: string
	target?: "_self" | "_blank"
	onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void
	className?: string
	disabled?: boolean
}

/**
 * 通用查看更多按钮组件
 *
 * PC端（1920px基准）：
 * - 最小宽度：160px
 * - 字体：DINPro-Regular, 400, 16px, 行高16px
 * - 颜色：#000000
 * - 内边距：16px 44px
 * - hover：黑色背景，白色文字
 *
 * 移动端（750px基准）：
 * - 最小宽度：216px
 * - 字体：DINPro-Regular, 400, 22px, 行高22px
 * - 颜色：#000000
 * - 内边距：29px 57px
 * - hover：黑色背景，白色文字
 */
const CommonViewMoreButton = ({
	children,
	href = "#",
	target = "_self",
	onClick,
	className,
	disabled = false,
}: CommonViewMoreButtonProps) => {
	const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
		if (disabled) {
			e.preventDefault()
			return
		}
		if (onClick) {
			onClick(e)
		}
	}

	return (
		<LocaleLink
			href={href}
			target={target === "_blank" ? "_blank" : undefined}
			rel={target === "_blank" ? "noopener noreferrer" : undefined}
			onClick={handleClick}
			className={cn(
				// 基础样式
				"inline-flex items-center justify-center",
				"border border-black rounded",
				"font-['DINPro-Regular'] font-normal",
				"text-right font-normal",
				"transition-all duration-300",
				"whitespace-nowrap",
				// 禁用状态
				disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer",
				// 移动端样式（750px基准）
				"min-w-[calc(216/750*100vw)]",
				"py-[calc(29/750*100vw)] px-[calc(57/750*100vw)]",
				"text-[calc(22/750*100vw)] leading-[calc(22/750*100vw)]",
				"text-[#000000]",
				// PC端样式（1920px基准，md断点及以上）
				"md:min-w-[min(calc(160/1920*100vw),160px)]",
				"md:py-[min(calc(16/1920*100vw),16px)] md:px-[min(calc(44/1920*100vw),44px)]",
				"md:text-[min(calc(16/1920*100vw),16px)] md:leading-[min(calc(16/1920*100vw),16px)]",
				"md:text-[#000000]",
				// hover效果 - 只在非禁用状态下生效
				!disabled &&
					"hover:bg-[#000000] hover:text-white hover:font-['DINPro-Medium']",
				className,
			)}
		>
			{children}
		</LocaleLink>
	)
}

export default CommonViewMoreButton
